<template>
  <SvgSprite />
  <div class="as-content-page as-match-detail" ref="asMatchDetail">
    <TabsIcon
      :tabs="tabs"
      :activeTab="activeTab"
      :isMin="isChatTyping"
      @change="changeTab"
    />

    <div
      id="as-match-detail-content"
      class="as-match-detail-content"
      :class="{
        'as-hide-content': isHideContent,
      }"
      :style="{
        height: `${heightContent}px`,
      }"
    >
      <div v-show="activeTab === 'chat'">
        <IframeChat />
      </div>
      <div v-if="activeTab === 'footStats'">
        <Transition name="as-fade" mode="out-in">
          <FootStatsList
            v-if="tabLoading.footStats"
            :match="match"
            :footyStats="footStats"
          />
          <H2hListSkeleton v-else />
        </Transition>
      </div>
      <div class="as-h-100" v-if="activeTab === 'statistics'">
        <Transition name="as-fade" mode="out-in">
          <StatisticsList
            v-if="tabLoading.statistics"
            :match="match"
            :statistics="statistics"
            :isTabCenter="true"
          />
          <StatisticsListSkeleton v-else />
        </Transition>
      </div>
      <div v-else-if="activeTab === 'overview'" class="as-h-100">
        <Transition name="as-fade" mode="out-in">
          <IncidentsList
            v-if="tabLoading.overview"
            :match="match"
            :incidents="incidents"
          />
          <IncidentsListSkeleton v-else />
        </Transition>
      </div>
      <div v-else-if="activeTab === 'lineups'">
        <LineupTab
          v-if="tabLoading.lineups"
          :match="match"
          :lineups="lineups"
        />
        <LineupTabSkeleton v-else />
      </div>
      <div v-else-if="activeTab === 'standing'">
        <Transition name="as-fade" mode="out-in">
          <StandingListTab
            v-if="tabLoading.standing"
            :match="match"
            :standing="standing"
          />
          <StandingListTabSkeleton v-else />
        </Transition>
      </div>
      <div v-else-if="activeTab === 'h2h'">
        <Transition name="as-fade" mode="out-in">
          <H2hList
            v-if="tabLoading.h2h || tabLoading.next_match || isObjectNotEmpty(h2h)"
            :match="match"
            :h2h="h2h"
            :isTabCenter="true"
          />
          <H2hListSkeleton v-else />
        </Transition>
      </div>
      <div
        v-else-if="activeTab === 'next_match'"
        :class="`as-tab-btn-center as-parent-h2h as-parent-h2h-${siteName}`"
      >
        <Transition name="as-fade" mode="out-in">
          <NextMatchList
            v-if="tabLoading.h2h || tabLoading.next_match || isObjectNotEmpty(h2h)"
            :match="match"
            :h2h="h2h"
          />
          <H2hListSkeleton v-else />
        </Transition>
      </div>
      <div
        v-else-if="activeTab === 'match_other'"
        :class="`as-parent-h2h as-parent-h2h-${siteName}`"
      >
        <Transition name="as-fade" mode="out-in">
          <MatchOther
            v-if="tabLoading.match_other"
            :matchLiveData="matchOtherData"
            :matchId="matchId"
          />
          <H2hListSkeleton v-else />
        </Transition>
      </div>
      <div v-else-if="activeTab === 'data'">
        <Data :match="match" :data="data" />
      </div>
      <div v-else-if="activeTab === 'odd'">
        <OddTab
          :odd="odd"
          :oddOld="oddOld"
          :tabKey="tabKey.odd"
        />
      </div>
      <div v-else-if="activeTab === 'information'">
        <Information :match="match" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import IncidentsListSkeleton from "~src/components/skeleton/result-popup/IncidentsListSkeleton.vue";
import StatisticsListSkeleton from "~src/components/skeleton/result-popup/StatisticsListSkeleton.vue";
import H2hListSkeleton from "~src/components/skeleton/result-popup/H2hListSkeleton.vue";
import LineupTabSkeleton from "~src/components/skeleton/result-popup/LineupTabSkeleton.vue";
import StandingListTabSkeleton from "~src/components/skeleton/standing/StandingListTabSkeleton.vue";
import {
  matchIncidents,
  matchStatistics,
  matchH2h,
  matchStanding,
  matchLineups,
  matchDetail,
  matchDetailLive,
} from "~src/services/SpapiService";
import { getOddsMatch } from "~src/services/OddService";
import { getMatchLive } from "~src/services/RemoteLiveService";
import {
  addMatchDetailLive,
  addIncidentToLineup,
  isObjectNotEmpty,
} from "~src/helpers";
import type {
  IMatch,
  IMatchLiveData,
  IOddMatch,
  IItemString,
} from "~src/interfaces/index";
import IncidentsList from "~src/components/incidents/IncidentsList.vue";
import StatisticsList from "~src/components/statistics/StatisticsList.vue";
import H2hList from "~src/components/h2h/H2hList.vue";
import StandingListTab from "~src/components/standings/StandingListTab.vue";
import LineupTab from "~src/components/lineups/LineupTab.vue";
import Information from "~src/components/matches/Information.vue";
import MatchOther from "~src/components/matches/MatchOther.vue";
import NextMatchList from "~src/components/matches/NextMatchList.vue";
import OddTab from "~src/components/odds/OddTab.vue";
import Data from "~src/components/datas/Data.vue";
import IframeChat from "~src/components/IframeChat.vue";
import IconStats from "~src/components/icons/menus/IconStats.vue";
import IconSummary from "~src/components/icons/menus/IconSummary.vue";
import IconLineUp from "~src/components/icons/menus/IconLineUp.vue";
import IconStanding from "~src/components/icons/menus/IconStanding.vue";
import IconChat from "~src/components/icons/menus/IconChat.vue";
import IconH2h from "~src/components/icons/menus/IconH2h.vue";
import IconMatchOther from "~src/components/icons/menus/IconMatchOther.vue";
import IconOdd from "~src/components/icons/menus/IconOdd.vue";
import IconInfo from "~src/components/icons/menus/IconInfo.vue";
import IconLive from "~src/components/icons/menus/IconLive.vue";
// import IconData from "~src/components/icons/menus/IconData.vue";
import SvgSprite from "~src/components/matches/SvgSprite.vue";
import TabsIcon from "~src/components/TabsIcon.vue";
import MatchEnum from "~src/enums/MatchEnum";
import { useMatchDetailStore } from "~src/stores/MatchDetailStore";
import { getMatchDetail } from "~src/services/FootystatsService";
import { EVENT_ODDS_LIVE_DATA_UPDATED } from "~src/utils/constant";
import FootStatsList from "~src/components/foot_stats/FootStatsList.vue";
import type { IFootyStats } from "~src/interfaces/footystats";

export default defineComponent({
  name: "matchDetail",
  components: {
    StatisticsListSkeleton,
    IncidentsListSkeleton,
    H2hListSkeleton,
    LineupTabSkeleton,
    StandingListTabSkeleton,
    IncidentsList,
    StatisticsList,
    H2hList,
    StandingListTab,
    LineupTab,
    MatchOther,
    NextMatchList,
    OddTab,
    Information,
    Data,
    IframeChat,
    SvgSprite,
    TabsIcon,
    FootStatsList,
  },
  props: {
    matchId: {
      type: String,
    },
    apiLiveUrl: {
      type: String,
    },
  },
  data() {
    const matchDetailStore = useMatchDetailStore();

    return {
      matchDetailStore,
      match: {} as IMatch,
      footStats: {} as IFootyStats,
      incidents: [],
      statistics: [] as any[],
      h2h: {},
      standing: {},
      lineups: {},
      data: null,
      odd: [] as IOddMatch[],
      oddOld: [] as IOddMatch[],
      matchOtherData: {} as IMatchLiveData,
      matchOthers: [] as Array<IMatch>,
      isLoading: false,
      isLoadingInterval: false,
      isShowChat: true,
      tabs: [
        // {
        //   key: "footStats",
        //   title: "Statics",
        //   icon: IconFootStats,
        // },
        {
          key: "statistics",
          title: "Thông số",
          icon: IconStats,
        },
        {
          key: "overview",
          title: "Diễn biến",
          icon: IconSummary,
        },
        {
          key: "lineups",
          title: "Đội hình",
          icon: IconLineUp,
        },
        {
          key: "standing",
          title: "BXH",
          icon: IconStanding,
        },
        {
          key: "chat",
          title: "Chat",
          icon: IconChat,
        },
        {
          key: "h2h",
          title: "Đối đầu",
          icon: IconH2h,
        },
        {
          key: "match_other",
          title: "Trận khác",
          icon: IconLive,
        },
        {
          key: "next_match",
          title: "Trận sắp tới",
          icon: IconMatchOther,
        },
        {
          key: "odd",
          title: "Tỷ lệ kèo",
          icon: IconOdd,
        },
        {
          key: "information",
          title: "TT trận",
          icon: IconInfo,
        },
        // {
        //   key: "data",
        //   title: "Dữ liệu",
        //   icon: IconData,
        // },
      ],
      tabLoading: {} as IItemString,
      tabKey: {} as IItemString,
      timers: [] as NodeJS.Timeout[]
    };
  },
  computed: {
    activeTab(): string {
      return this.matchDetailStore.getActiveTab;
    },
    isChatTyping(): boolean {
      return this.matchDetailStore.getIsChatTyping;
    },
    isHideContent(): boolean {
      return this.matchDetailStore.getIsHideContent;
    },
    heightContent(): number {
      return this.matchDetailStore.getHeightContent;
    },
    sportType() {
      // @ts-ignore
      return this.gSportType;
    },
    siteName() {
      // @ts-ignore
      return this.gSiteName;
    },
    isLiveSite(): boolean {
      // @ts-ignore
      return this.gIsLiveSite;
    },
  },
  async created() {
    try {
      const rsMatch = await matchDetail(this.matchId, this.sportType);
      this.match =
        rsMatch.data.data && rsMatch.data.data.data
          ? rsMatch.data.data.data
          : rsMatch.data.data;
      await this.changeTab("chat");
    } catch (e) {
      console.log("Load match detail error", e);
    }
  },
  async mounted() {
    const element = this.$refs.asMatchDetail as HTMLElement;
    if (element) {
      // @ts-ignore
      this.matchDetailStore.setScreenWidth(
        element?.getBoundingClientRect().width
      );
    }

    const interval1 = setInterval(async () => {
      if (
        this.match.status_id !== MatchEnum.STATUS_END &&
        !this.isLoadingInterval &&
        this.activeTab !== "odd"
      ) {
        this.isLoadingInterval = true;
        await this.changeTab(this.activeTab, true);
        this.isLoadingInterval = false;
      }
    }, 21000);
    this.timers.push(interval1);

    if (this.isLiveSite) {
      window.addEventListener(EVENT_ODDS_LIVE_DATA_UPDATED, this.handleOddsLiveDataUpdated);
    } else {
      const interval2 = setInterval(async () => {
        if (
          this.match.status_id !== MatchEnum.STATUS_END &&
          !this.isLoadingInterval &&
          this.activeTab === "odd"
        ) {
          this.isLoadingInterval = true;
          await this.changeTab(this.activeTab, true);
          this.isLoadingInterval = false;
        }
      }, 5000);
      this.timers.push(interval2);
    }
  },
  async beforeUnmount() {
    this.timers.forEach(clearInterval);

    if (this.isLiveSite) {
      window.removeEventListener(EVENT_ODDS_LIVE_DATA_UPDATED, this.handleOddsLiveDataUpdated);
    }
  },
  methods: {
    async changeTab(tabName: string, isInterval = false) {
      this.matchDetailStore.setActiveTab(tabName);
      const matchId = this.matchId;
      const seasonId = this.match?.season_id ?? "";
      this.isLoading = true;

      if (tabName === "statistics") {
        try {
          const { data: { data: statistics = [] } } = await matchStatistics(matchId);
          this.statistics = statistics || [];
        } catch (e) {
          console.log("Load statistics error", e);
        }
      }

      if (tabName === "overview") {
        try {
          const { data: { data: incidents = [] } } = await matchIncidents(matchId);
          this.incidents = incidents;
        } catch (e) {
          console.log("Load incidents error", e);
        }
      }

      if (["h2h", "next_match"].includes(tabName)) {
        try {
          const { data: { data: h2h = {} } } = await matchH2h(matchId);
          this.h2h = h2h;
        } catch (e) {
          console.log("Load h2h error", e);
        }
      }

      if (tabName === "match_other") {
        if (isInterval) {
          try {
            const rsLive = await matchDetailLive(this.sportType);
            const matches = await addMatchDetailLive(
              this.matchOtherData.matches,
              rsLive.data.results,
              this.sportType
            );
            this.matchOtherData.matches = matches;
          } catch (e) {
            console.log("Load live error", e);
          }
        } else {
          try {
            const [rsMatch, rsLive] = await Promise.all([
              getMatchLive(this.apiLiveUrl, this.sportType, this.siteName),
              matchDetailLive(this.sportType),
            ]);
            const matches = await addMatchDetailLive(
              rsMatch.data.matches,
              rsLive.data.results,
              this.sportType
            );
            this.matchOtherData = {
              competitions: rsMatch.data.competitions,
              teams: rsMatch.data.teams,
              commentators: rsMatch.data.commentators,
              matches,
            } as IMatchLiveData;
          } catch (e) {
            console.log("Load match other error", e);
          }
        }
      }

      if (tabName === "standing") {
        if (seasonId) {
          try {
            const { data: { data: standing = {} } } = await matchStanding(seasonId);
            this.standing = standing;
          } catch (e) {
            console.log("Load standing error", e);
          }
        }
      }

      if (tabName === "lineups") {
        try {
          const [rsLineups, rsIncident] = await Promise.all([
            matchLineups(matchId),
            matchIncidents(matchId),
          ]);
          const __lineups = addIncidentToLineup(
            rsLineups.data.data ? rsLineups.data.data : {},
            rsIncident.data.data ? rsIncident.data.data : []
          );

          this.lineups = __lineups;
          this.incidents = rsIncident.data.data ? rsIncident.data.data : [];
        } catch (e) {
          console.log("Load lineups error", e);
        }
      }

      if (tabName === "odd") {
        try {
          const rs = await getOddsMatch(this.sportType, matchId);
          this.oddOld = JSON.parse(JSON.stringify(this.odd));
          this.odd = rs.data.data;
        } catch (e) {
          console.log("Load odd error", e);
        }
      }

      if (tabName === "data") {
        // const rs = await getAnalysis(
        //   this.sportType,
        //   unReverseId(this.matchMapping.match_id)
        // );
        // this.data = rs.data.data;
      }

      if (tabName === "footStats") {
        try {
          const { data: { data: footStats = {} } } = await getMatchDetail(matchId);
          this.footStats = footStats;
        } catch (e) {
          console.log("Load footStats error", e);
        }
      }

      this.isLoading = false;
      this.tabKey[tabName] = Date.now();
      this.tabLoading[tabName] = true;
    },
    isObjectNotEmpty,
    handleOddsLiveDataUpdated(e: Event) {
      const oddsData = (e as CustomEvent<IOddMatch[]>).detail;
      if (oddsData && this.activeTab === "odd") {
        this.oddOld = JSON.parse(JSON.stringify(this.odd));
        this.odd = oddsData;
      }
    },
  },
});
</script>

<style scoped></style>
